import type { CSSProperties, ReactElement, ReactNode } from "react";

export interface IconProps {
  size?: number;
  sw?: number;
  fill?: string;
  style?: CSSProperties;
}

function I({ size = 20, sw = 2, fill = "none", style, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export type CFIconComponent = (props: IconProps) => ReactElement;

/** Lucide-style outline icon set (2px stroke, 24px grid) used across ClearFlow. */
export const Icons = {
  home: (p: IconProps) => (
    <I {...p}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </I>
  ),
  docs: (p: IconProps) => (
    <I {...p}>
      <path d="M14 3H6v18h12V7z" />
      <path d="M14 3v4h4" />
      <path d="M9 13h6M9 17h4" />
    </I>
  ),
  tasks: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M8 3v3M16 3v3M8 13l2.5 2.5L15 11" />
    </I>
  ),
  registry: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 4v16" />
    </I>
  ),
  search: (p: IconProps) => (
    <I {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </I>
  ),
  archive: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </I>
  ),
  reports: (p: IconProps) => (
    <I {...p}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </I>
  ),
  book: (p: IconProps) => (
    <I {...p}>
      <path d="M4 4v16a1 1 0 0 0 1 1h15V3H5a1 1 0 0 0-1 1z" />
      <path d="M9 3v18" />
    </I>
  ),
  helpCircle: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.3a2.8 2.8 0 0 1 5.4 1c0 1.9-2.8 2.8-2.8 2.8" />
      <path d="M12 17h.01" />
    </I>
  ),
  admin: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H1a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 2.6 7a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H7a1.6 1.6 0 0 0 1-1.5V1a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V7a1.6 1.6 0 0 0 1.5 1H23a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </I>
  ),
  audit: (p: IconProps) => (
    <I {...p}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </I>
  ),
  settings: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </I>
  ),
  bell: (p: IconProps) => (
    <I {...p}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </I>
  ),
  plus: (p: IconProps) => (
    <I {...p}>
      <path d="M12 5v14M5 12h14" />
    </I>
  ),
  smile: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <path d="M9 9h.01M15 9h.01" />
    </I>
  ),
  chevronDown: (p: IconProps) => (
    <I {...p}>
      <path d="M6 9l6 6 6-6" />
    </I>
  ),
  chevronRight: (p: IconProps) => (
    <I {...p}>
      <path d="M9 6l6 6-6 6" />
    </I>
  ),
  chevronLeft: (p: IconProps) => (
    <I {...p}>
      <path d="M15 6l-6 6 6 6" />
    </I>
  ),
  filter: (p: IconProps) => (
    <I {...p}>
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </I>
  ),
  download: (p: IconProps) => (
    <I {...p}>
      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
    </I>
  ),
  upload: (p: IconProps) => (
    <I {...p}>
      <path d="M12 21V9M7 14l5-5 5 5M5 3h14" />
    </I>
  ),
  more: (p: IconProps) => (
    <I {...p}>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </I>
  ),
  menu: (p: IconProps) => (
    <I {...p}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </I>
  ),
  clock: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </I>
  ),
  check: (p: IconProps) => (
    <I {...p}>
      <path d="M20 6L9 17l-5-5" />
    </I>
  ),
  x: (p: IconProps) => (
    <I {...p}>
      <path d="M6 6l12 12M18 6L6 18" />
    </I>
  ),
  reply: (p: IconProps) => (
    <I {...p}>
      <path d="M9 17l-5-5 5-5" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </I>
  ),
  quote: (p: IconProps) => (
    <I {...p}>
      <path d="M3 21c3 0 7-1 7-8V5H4v6h4" />
      <path d="M14 21c3 0 7-1 7-8V5h-6v6h4" />
    </I>
  ),
  route: (p: IconProps) => (
    <I {...p}>
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M6 9v3a6 6 0 0 0 6 6h3" />
    </I>
  ),
  lock: (p: IconProps) => (
    <I {...p}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </I>
  ),
  user: (p: IconProps) => (
    <I {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
    </I>
  ),
  inbox: (p: IconProps) => (
    <I {...p}>
      <path d="M3 12h5l2 3h4l2-3h5" />
      <path d="M5 5h14l2 7v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-7z" />
    </I>
  ),
  chat: (p: IconProps) => (
    <I {...p}>
      <path d="M21 12a8 8 0 0 1-8 8H6l-4 2 1.4-4.2A9 9 0 1 1 21 12z" />
      <path d="M8 12h.01M12 12h.01M16 12h.01" />
    </I>
  ),
  send: (p: IconProps) => (
    <I {...p}>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />
    </I>
  ),
  projects: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M17.5 14v7M14 17.5h7" />
    </I>
  ),
  contacts: (p: IconProps) => (
    <I {...p}>
      <path d="M4 4v16a1 1 0 0 0 1 1h14V3H5a1 1 0 0 0-1 1z" />
      <circle cx="11" cy="10" r="2.4" />
      <path d="M7.5 16c.5-1.7 2-2.6 3.5-2.6s3 .9 3.5 2.6" />
      <path d="M19 7h2M19 12h2M19 17h2" />
    </I>
  ),
  pause: (p: IconProps) => (
    <I {...p}>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </I>
  ),
  play: (p: IconProps) => (
    <I {...p}>
      <path d="M7 4l13 8-13 8z" />
    </I>
  ),
  stop: (p: IconProps) => (
    <I {...p}>
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </I>
  ),
  edit: (p: IconProps) => (
    <I {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </I>
  ),
  trash: (p: IconProps) => (
    <I {...p}>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M5 6l1 14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1l1-14" />
    </I>
  ),
  calendar: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </I>
  ),
  paperclip: (p: IconProps) => (
    <I {...p}>
      <path d="M21 11l-8.5 8.5a5 5 0 0 1-7-7L14 4a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />
    </I>
  ),
  refresh: (p: IconProps) => (
    <I {...p}>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </I>
  ),
  mail: (p: IconProps) => (
    <I {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </I>
  ),
  tag: (p: IconProps) => (
    <I {...p}>
      <path d="M3 11V4a1 1 0 0 1 1-1h7l9 9-8 8z" />
      <circle cx="7.5" cy="7.5" r="1.2" />
    </I>
  ),
  layers: (p: IconProps) => (
    <I {...p}>
      <path d="M12 2l9 5-9 5-9-5z" />
      <path d="M3 12l9 5 9-5M3 17l9 5 9-5" />
    </I>
  ),
  flag: (p: IconProps) => (
    <I {...p}>
      <path d="M4 22V4M4 4h13l-2 4 2 4H4" />
    </I>
  ),
  arrowRight: (p: IconProps) => (
    <I {...p}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </I>
  ),
  externalLink: (p: IconProps) => (
    <I {...p}>
      <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </I>
  ),
  fund: (p: IconProps) => (
    <I {...p}>
      <path d="M4 21h16M4 21V9l8-5 8 5v12M9 21v-7h6v7" />
    </I>
  ),
} satisfies Record<string, CFIconComponent>;

export type IconName = keyof typeof Icons;
